<template>
    <div v-if="$page.props.auth.check">
        <form-modal :show="show" :closeable="false" max-width="xl" @submit="handleSubmit" @close="close" @after-close="form.amount = amount">
            <template #content>
                <div class="-mb-2">
                    <div class="flex items-center justify-end -mr-2 -mt-2">
                        <modal-close @close="close" />
                    </div>

                    <div v-if="!amountSet" class="grid gap-2 grid-cols-7">
                        <div class="col-span-7 text-center sm:col-span-5 sm:col-start-2">
                            <label :for="'amount-' + uuid" class="block text-gray-700 dark:text-slate-400 text-2xl">How Many Tokens?</label>
                        </div>

                        <div class="col-span-7 text-center rounded-md sm:col-span-5 sm:col-start-2">
                            <div class="flex mt-1 rounded-md">
                                <button
                                    v-long-press="decreaseAmountByTen"
                                    class="relative inline-flex items-center -mr-px px-4 py-2 hover:text-blue-700 text-gray-600 font-medium hover:bg-gray-100 bg-gray-50 border active:border-blue-500 border-gray-300 rounded-l-md focus:outline-none space-x-2 focus:ring-1 active:ring-blue-500 prevent-select"
                                    @click.prevent="decreaseAmount"
                                >
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M18 12H6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                    </svg>
                                    <span class="sr-only">Decrease</span>
                                </button>
                                <div class="focus-within:z-10">
                                    <input :id="'amount-' + uuid" v-model="form.amount" class="block w-full text-center text-5xl border-gray-300 focus:border-indigo-500 rounded-none focus:ring-indigo-500" :maxlength="maxLength" placeholder="5" type="text" @blur="validateAmount" />
                                </div>
                                <button
                                    v-long-press="increaseAmountByTen"
                                    class="relative inline-flex items-center -ml-px px-4 py-2 hover:text-blue-700 text-gray-600 font-medium hover:bg-gray-100 bg-gray-50 border active:border-blue-500 border-gray-300 rounded-r-md focus:outline-none space-x-2 focus:ring-1 active:ring-blue-500 prevent-select"
                                    @click.prevent="increaseAmount"
                                >
                                    <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                    </svg>
                                    <span class="sr-only">Increase</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="amountSet" class="grid gap-3 grid-cols-7">
                        <div class="col-span-7 text-center sm:col-span-5 sm:col-start-2">
                            <label class="block text-gray-700 dark:text-slate-400 font-display text-2xl font-medium">Choose Payment Method</label>
                        </div>
                        <div class="col-span-5 col-start-2 text-center text-gray-600 dark:text-slate-300 text-sm font-medium">You are purchasing {{ form.amount }} tokens</div>
                        <credit-card-form ref="creditCardFormRef" @paymentResponse="chargePaymentMethod" v-if="show" :amount="form.amount" :processing="form.processing" class="col-span-7 text-center"></credit-card-form>
                        <input-error :message="form.errors.amount" class="col-span-7 mt-2 text-center" />
                        <input-error v-if="$page.props.flash.error" :message="$page.props.flash.error" class="col-span-7 mt-2 p-3 text-center font-medium bg-rose-100 rounded-md" />
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex flex-col-reverse justify-between align-middle w-full sm:flex-row sm:items-center">
                    <div v-if="amountSet" class="flex items-center mt-4 text-blue-400 hover:text-blue-600 cursor-pointer space-x-1 sm:mt-0" @click="amountSet = false">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        <span>Change Amount</span>
                    </div>

                    <div class="flex w-full sm:ml-auto sm:w-auto">
                        <a
                            href="#"
                            v-if="!amountSet"
                            class="brand-button focus:shadow-outline-gray duration-50 relative inline-flex items-center align-middle place-content-center px-6 py-3 w-full text-white text-sm font-semibold tracking-wide border border-transparent rounded-md focus:outline-none uppercase transition ease-in-out sm:w-auto"
                            @click.prevent="setAmount"
                        >
                            <span>Payment Method</span>
                            <svg class="-mr-1 ml-3 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </a>

                        <jet-button v-if="amountSet" :processing="form.processing" class="w-full sm:w-auto"> Pay {{ form.amount | currency }} </jet-button>
                    </div>
                </div>
            </template>
        </form-modal>

        <buy-tokens-successful-modal v-if="showBuyTokensSuccessfulModal" :show.sync="showBuyTokensSuccessfulModal" :amount="buyTokensSuccessfulModalAmount" />
    </div>
</template>

<script>
import JetButton from "@/Jetstream/Button"
import JetLabel from "@/Jetstream/Label"
import JetInput from "@/Jetstream/Input"
import JetInputError from "@/Jetstream/InputError"
import InputError from "@/Jetstream/InputError"
import { LongPress } from "@/Directives/LongPress"
import { CurrencyDirective } from "vue-currency-input"
import CreditCardForm from "@/Components/CreditCardForm"
import ModalClose from "@/Components/ModalClose"
import { uuid } from "vue-uuid"
import BuyTokensSuccessfulModal from "@/Pages/Wallet/BuyTokensSuccessfulModal"
import FormModal from "@/Components/FormModal"

export default {
    directives: {
        "long-press": LongPress,
        currency: CurrencyDirective,
    },

    components: {
        InputError,
        FormModal,
        BuyTokensSuccessfulModal,
        ModalClose,
        CreditCardForm,
        JetInputError,
        JetInput,
        JetLabel,
        JetButton,
    },

    props: {
        show: {
            default: true,
            type: Boolean,
        },
        amount: {
            default: 5,
            type: Number,
        },
    },

    computed: {
        // form() {
        //     return this.$inertia.form({
        //         _method: "PUT",
        //         amount: this.amount < this.minimumPurchaseAmount ? this.minimumPurchaseAmount : this.amount,
        //         payment_method: null,
        //     })
        // },

        maxLength() {
            return this.$page.props.user.id === 1 ? "10" : "4"
        },
    },

    data() {
        return {
            uuid: uuid.v4(),
            minimumPurchaseAmount: 5,
            paymentResponse: null,
            paymentMethod: null,
            amountSet: false,
            currencyOptions: {
                precision: 0,
                currency: null,
                locale: "en",
                allowNegative: false,
                valueAsInteger: true,
            },
            showBuyTokensSuccessfulModal: false,
            buyTokensSuccessfulModalAmount: 0,
            form: this.$inertia.form({
                _method: "PUT",
                amount: this.amount,
                payment_method: null,
            }),
        }
    },

    methods: {
        handleSubmit() {
            this.$refs.creditCardFormRef.submit()
        },

        chargePaymentMethod({ paymentMethod }) {
            this.form.payment_method = paymentMethod
            this.form.post(route("wallet.tokens.purchase"), {
                onSuccess: () => {
                    this.paymentStatus()
                },
                resetOnSuccess: false,
            })
        },

        paymentStatus() {
            if (this.$page.props.flash.success) {
                this.buyTokensSuccessfulModalAmount = this.form.amount
                this.close()
                setTimeout(() => {
                    this.showBuyTokensSuccessfulModal = true
                }, 500)
            }
        },

        close() {
            this.$emit("update:show", false)
        },

        increaseAmount() {
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value += 5
            this.form.amount = value
        },

        increaseAmountByTen() {
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value += 10
            this.form.amount = value
        },

        decreaseAmount() {
            if (this.form.amount <= 5) return false
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value < 1 ? (value = 1) : ""
            value -= 5
            this.form.amount = value
        },

        decreaseAmountByTen() {
            if (this.form.amount <= 5) return false
            let value = parseInt(this.form.amount, 10)
            value = isNaN(value) ? 0 : value
            value < 1 ? (value = 1) : ""
            value -= 10
            this.form.amount = value
        },

        validateAmount() {
            if (this.form.amount < 5) {
                this.form.amount = 5
            }
        },

        setAmount() {
            this.amountSet = true
        },
    },

    beforeDestroy() {
        this.form.reset()
    },
}
</script>

<style scoped>
.prevent-select {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
}
</style>
